import { getCompanyOptionWithPriceSelect } from '@/api/get/config/get-company-options-with-price-select';
import OptionSVG from '@/assets/svg/icon_options.svg';
import { PrimaryOutlineButton } from '@/components/button/button-component';
import OptionModal from '@/pages/company-management/company-options/option-modal';
import { Heading2Text } from '@/utils/global-fonts';
import { extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/list-item';
import { GroupDropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/group';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { CiFolderOn } from 'react-icons/ci';
import { IoMdAdd, IoMdArrowDropdown } from 'react-icons/io';
import {
  MdOutlineAddCircleOutline,
  MdOutlinePlaylistAdd,
} from 'react-icons/md';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { v4 } from 'uuid';
import PopUpMenu from '../../pop-up-menu/pop-up-menu-component';
import { useFieldArrayRegistry } from './context-provider';
import OptionStructure from './options-structure-component';
import ProductOptionModal from './product-option-modal';
import { styledOption as Styled } from './product-options-style';
import { getSelectedOption } from './products-utils';
/**
 * Represents a hierarchical structure of files and folders.
 * @param {object} props - The context to use.
 * @param {string} props.name - The hook form name.
 * @param {object} props.CONTEXT - The context to use.
 * @param {Function} props.handleRevert - The handle revert function to use.
 * @returns {JSX.Element}
 */
const ProductOptionsComponent = ({ name, CONTEXT, handleRevert }) => {
  const { id: companyId } = useParams();
  const folderOpenRef = useRef({});
  const mainRef = useRef(null);
  const groupRef = useRef(null);
  const { control, trigger, getValues } = useFormContext();
  const { options, setOptions, uniqueContextId } = useContext(CONTEXT);
  const [dropTargetState, setDropTargetState] = useState(null);
  const addOptionsRef = useRef(null);
  const addRef = useRef(null);
  const registry = useFieldArrayRegistry();

  const methods = useFieldArray({
    control,
    name, // unique name for your Field Array
  });

  const { fields, append } = methods;

  const [isOpen, setIsOpen] = useState(false);
  const [isOpenAddOption, setIsOpenAddOption] = useState(false);
  const [isOpenCreateNewOption, setIsOpenCreateNewOption] = useState({
    label: '',
    id: null,
  });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleAddNewButtonClick = (e) => {
    if (!isOpen) {
      const pos = e.target.getBoundingClientRect();
      const x = pos.left + pos.width;
      const y = pos.bottom - pos.top + 210;
      setPosition({ x, y });
    } else {
      setPosition({ x: 0, y: 0 });
    }

    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the popup container
      const isOutsideContainer =
        addOptionsRef.current && !addOptionsRef.current.contains(event.target);

      // Check if click is on a popup menu item (portal content)
      const isPopupMenuItem =
        event.target.closest('[data-popup-menu-item]') ||
        event.target.closest('.popup-menu-item') ||
        event.target.closest('[role="menuitem"]');

      // Only close if clicking outside AND not on a popup menu item
      if (isOutsideContainer && !isPopupMenuItem) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCreateOptionModal = () => {
    setIsOpenCreateNewOption({ label: 'Add', id: null, root: true });
  };

  const closeCreateOptionModal = useCallback(() => {
    setIsOpenCreateNewOption({ label: '', id: null });
  }, []);

  const addOptionMenu = [
    {
      label: 'Folder',
      icon: <CiFolderOn />,
      action: () => {
        append({
          option_type: 'folder',
          select_type: 'multi',
          is_required: false,
          children: [],
          id: v4(),
          name: '',
        });
      },
    },
    {
      label: 'Add Option',
      icon: <MdOutlinePlaylistAdd />,
      action: () => {
        setIsOpenAddOption(true);
      },
    },
    {
      label: 'Create New Option',
      icon: <MdOutlineAddCircleOutline />,
      action: () => handleCreateOptionModal(),
    },
  ];

  const handleAddOption = (submitValues) => {
    const option = getSelectedOption(
      options,
      submitValues.option_price_link_id,
    );
    append({
      option_type: 'option',
      id: v4(),
      parent_id: undefined,
      select_type: 'multi',
      is_required: false,
      children: [],
      name: option?.name,
      option_price_link_id: submitValues.option_price_link_id,
      dependencies: submitValues.dependencies,
    });
    trigger(name);
    setIsOpenAddOption(false);
  };

  // Create option and assign to root
  const handleOptionCreated = useCallback(
    (values) => {
      if (companyId && setOptions) {
        getCompanyOptionWithPriceSelect(companyId).then((response) => {
          setOptions(response.data);

          const option = getSelectedOption(
            response.data,
            values.optionPriceLinkId,
          );

          const isVariant =
            response.data.find(
              (responseOption) => responseOption.id === values.optionId,
            )?.isVariant ?? false;

          append({
            option_type: 'option',
            id: v4(),
            parent_id: undefined,
            select_type: 'multi',
            is_required: false,
            children: [],
            name: option?.name,
            option_price_link_id: values.optionPriceLinkId,
            dependencies: [],
            isVariant,
            option_id: values.optionId,
          });
          // Close the modal
          closeCreateOptionModal();
        });
      }
    },
    [companyId, setOptions, closeCreateOptionModal, append],
  );

  useEffect(() => {
    function onDropTargetChange({ location, self }) {
      const [innerMost] = location.current.dropTargets.filter(
        (dropTarget) => dropTarget.data.type === 'group',
      );

      setDropTargetState(
        innerMost?.element === self.element ? 'is-innermost-over' : 'idle',
      );
    }

    if (mainRef?.current && groupRef?.current) {
      invariant(mainRef.current);
      invariant(groupRef.current);

      return combine(
        monitorForElements({
          canMonitor: ({ source }) =>
            source.data.uniqueContextId === uniqueContextId &&
            source.data.type === 'tree-item',
          onDrop(args) {
            const { location, source } = args;
            // didn't drop on anything
            if (!location.current.dropTargets.length) {
              return;
            }

            const target = location.current.dropTargets[0];

            // const sourceIndex = parseInt(source.data.index, 10);
            // const targetIndex = parseInt(target.data.index, 10);
            const sourceName = source.data.name
              .split('.')
              .slice(0, -1)
              .join('.');
            const targetName = target.data.name
              .split('.')
              .slice(0, -1)
              .join('.');

            const sourceValues = getValues(source.data.name);

            const instruction = extractInstruction(target.data);

            const moveAndReorder = sourceName !== targetName;

            if (instruction?.operation === 'combine') {
              // This means we need to remove it from the source and add it to the target.
              const sourceFieldArray = registry.get(sourceName);
              const targetFieldArray = registry.get(target.data.dropName);
              const targetParentName = target.data.dropName
                .split('.')
                .slice(0, -1)
                .join('.');
              const parentValues = getValues(targetParentName);
              sourceValues.parent_id = parentValues.id;
              sourceValues.order = parentValues.children.length + 1;
              if (targetFieldArray) {
                targetFieldArray.append(sourceValues);
              } else {
                // Parent field array and index to update.
                const parentIndex = targetParentName.split('.').pop();
                const grandParentName = targetParentName
                  .split('.')
                  .slice(0, -1)
                  .join('.');
                const grandParentFieldArray = registry.get(grandParentName);
                grandParentFieldArray.update(parentIndex, {
                  ...parentValues,
                  children: [...(parentValues.children ?? []), sourceValues],
                });
              }
              sourceFieldArray.remove(source.data.index);
            } else if (
              (instruction?.operation === 'reorder-before' ||
                instruction?.operation === 'reorder-after') &&
              moveAndReorder
            ) {
              const sourceFieldArray = registry.get(sourceName);
              const targetFieldArray = registry.get(targetName);
              const targetParentName = targetName
                .split('.')
                .slice(0, -1)
                .join('.');
              const parentValues = getValues(targetParentName ?? targetName);
              sourceValues.parent_id = parentValues?.id ?? null;
              let insertIndex = target.data.index;
              if (instruction?.operation === 'reorder-after') {
                insertIndex += 1;
              }
              sourceValues.order = insertIndex + 1;
              targetFieldArray.insert(insertIndex, sourceValues);
              sourceFieldArray.remove(source.data.index);
            } else {
              const sourceFieldArray = registry.get(sourceName);
              sourceFieldArray.move(source.data.index, target.data.index);
            }
          },
        }),
        dropTargetForElements({
          element: groupRef.current,
          canDrop: ({ source }) =>
            source.data.uniqueContextId === uniqueContextId &&
            source.data.type === 'tree-item',
          getData: () => ({ type: 'group' }),
          onDragStart: onDropTargetChange,
          onDropTargetChange,
          onDragLeave: () => setDropTargetState('idle'),
          onDrop: () => setDropTargetState('idle'),
        }),
      );
    }
  }, [getValues, mainRef, name, registry, trigger, uniqueContextId]);

  if (fields?.length === 0) {
    return (
      <Styled.NoOptionAvailableWrapper>
        <Styled.NoOptionImg src={OptionSVG} alt="No option img" />
        <Heading2Text>No Options</Heading2Text>
        <Styled.StyledBodyText>
          You have not linked any options to this product yet. Click below to
          start adding some options and categories
        </Styled.StyledBodyText>
        <Styled.ButtonContainer ref={addOptionsRef}>
          <PrimaryOutlineButton
            title="Add Options"
            icon={<IoMdAdd size={16} />}
            margin="10px 0px 0px 0px"
            onClick={handleAddNewButtonClick}
          />
          <PopUpMenu
            isOpen={isOpen}
            top={`${position.y}`}
            left={`${position.x}`}
            onClose={() => setIsOpen(false)}
            menuItems={addOptionMenu}
          />
          <ProductOptionModal
            isOpen={isOpenAddOption}
            name={name}
            setIsOpen={setIsOpenAddOption}
            onSubmit={handleAddOption}
            CONTEXT={CONTEXT}
          />
          <OptionModal
            modalAction={isOpenCreateNewOption}
            closeModal={closeCreateOptionModal}
            onCreate={handleOptionCreated}
          />
        </Styled.ButtonContainer>
      </Styled.NoOptionAvailableWrapper>
    );
  }
  return (
    <Styled.Container>
      <Styled.Wrapper>
        <div ref={mainRef}>
          <GroupDropIndicator
            isActive={dropTargetState === 'is-innermost-over'}
            ref={groupRef}
          >
            <OptionStructure
              name={`${name}`}
              indent={1}
              options={methods}
              CONTEXT={CONTEXT}
              handleRevert={handleRevert}
              folderOpenRef={folderOpenRef}
              mainRef={mainRef}
            />
          </GroupDropIndicator>
        </div>
      </Styled.Wrapper>
      <Styled.AddWrapper ref={addRef}>
        <Styled.AddNewOptionButton
          onClick={handleAddNewButtonClick}
          role="none"
        >
          <Styled.ButtonLabelWrapper>
            <IoMdAdd /> Add
          </Styled.ButtonLabelWrapper>
          <IoMdArrowDropdown className="icon arrow-icon" />
        </Styled.AddNewOptionButton>
        <PopUpMenu
          isOpen={isOpen}
          top={`${position.y}`}
          left={`${position.x}`}
          onClose={() => setIsOpen(false)}
          menuItems={addOptionMenu}
        />
        <ProductOptionModal
          isOpen={isOpenAddOption}
          onSubmit={handleAddOption}
          setIsOpen={setIsOpenAddOption}
          name={name}
          CONTEXT={CONTEXT}
        />
        <OptionModal
          modalAction={isOpenCreateNewOption}
          closeModal={closeCreateOptionModal}
          onCreate={handleOptionCreated}
        />
      </Styled.AddWrapper>
    </Styled.Container>
  );
};

ProductOptionsComponent.propTypes = {
  name: PropTypes.string.isRequired,
  CONTEXT: PropTypes.object.isRequired,
  handleRevert: PropTypes.func.isRequired,
};
export default ProductOptionsComponent;
